// src/seo/eventPages.js
//
// Single source of truth for the SEO landing pages: one per feature
// (events, registration, ticketing, raffles, auctions, membership, social)
// and one per audience (nonprofits, churches, schools). Each targets the
// searches people make for the problem, not the brand name. Read by:
//   * components/seo/EventSoftwarePage.jsx — the React page.
//   * scripts/buildSeoPages.mjs (postbuild) — writes build/seo/event-pages.json,
//     which server/routes/publicPageRoutes.js uses to send these paths with
//     their own <head> tags, JSON-LD and crawler-readable body HTML, and to
//     list them in /sitemaps/_site.xml.
//
// Must stay plain ESM with no imports: the build script loads it in Node.
//
// Every feature claim maps to a section of the user guide (/docs) — keep it
// that way. If a feature changes, edit it here; nothing else holds copy.
//
// Per page: `group` ('feature' | 'audience') sorts it in the footer link
// lists; `nav: true` also puts it in the header nav (kept short for phones);
// `category` is the JSON-LD applicationSubCategory; `finalTitle` overrides
// the closing call-to-action heading.

export const SITE = 'https://on-screen.app';
export const OG_IMAGE = `${SITE}/on-screen-logo-white-bezel-fullres.png`;
export const SIGNUP_URL = '/owneradd';
export const DOCS_URL = '/docs/index.html';

const DOC = (file) => `/docs/${file}`;

// Shared blocks -------------------------------------------------------------

const STEP_CREATE = {
  title: 'Create the event',
  body: 'Add the event with its date, venue, and description, then add items to it. An item can be a ticketed event, an auction, a raffle, a contest, a poll, or another event action — so one event can hold a show, a silent auction, and a raffle at the same time.',
  doc: DOC('OnScreen_11_Events.html'),
};

const STEP_REGISTER = {
  title: 'Open registration',
  body: 'Collect name, email, and phone, plus any custom questions you need — dietary restrictions, T-shirt size, emergency contact — each marked required or optional.',
  doc: DOC('OnScreen_20_Ticketing_Check_in.html'),
};

const STEP_TICKETS = {
  title: 'Sell tickets',
  body: 'Set up priced ticket tiers with their own capacity, add promo codes (fixed amount or percent off, with an optional redemption cap), and let buyers join a waitlist when a tier sells out. Payments go to the Stripe or Square account you connect.',
  doc: DOC('OnScreen_20_Ticketing_Check_in.html'),
};

const STEP_PROMOTE = {
  title: 'Promote it',
  body: 'Share the public event page by link or QR code. A marketing campaign builds a promotion timeline backward from the event date and schedules posts to your connected social accounts. The ticketing dashboard shows where buyers came from.',
  doc: DOC('OnScreen_28_Marketing_Campaigns.html'),
};

const STEP_ATTENDEES = {
  title: 'Manage attendees',
  body: 'See every guest in one filterable roster. Buyers get a ticket wallet with one QR code per ticket and can transfer a ticket to someone else. Send complimentary tickets straight to an email address — nothing is usable until the recipient confirms the claim link.',
  doc: DOC('OnScreen_30_Public_Event_Attendee_Pages.html'),
};

const STEP_CHECKIN = {
  title: 'Check guests in with QR codes',
  body: 'Staff scan tickets with a phone or tablet camera from a mobile-first door console. Use the manual override for anyone the scanner can’t read, and register walk-ins on the spot — comped or charged a tier.',
  doc: DOC('OnScreen_20_Ticketing_Check_in.html'),
};

const STEP_AFTER = {
  title: 'Wrap up and report',
  body: 'The Event Hub shows revenue, tickets sold, and check-in progress alongside orders, vendors, and budget. Afterward, publish a public recap page with the photos and clips you choose — even ones collected from a photographer through a no-account upload link.',
  doc: DOC('OnScreen_29_The_Event_Hub.html'),
};

const FAQ_ACCOUNT = {
  q: 'Do attendees need an account to register or buy tickets?',
  a: 'No. The public event page, registration form, and checkout work without a login. Buyers open their tickets from the confirmation email.',
};

const FAQ_DEVICES = {
  q: 'What do I need to run check-in at the door?',
  a: 'A phone, tablet, or laptop with a camera. On-Screen installs as a PWA on iOS, Android, Windows, and macOS — no app store needed.',
};

const FAQ_MODULAR = {
  q: 'Do I have to use the whole platform?',
  a: 'No. Use only what you need — ticketing and check-in, for example — and add membership, social scheduling, or auctions and raffles later.',
};

const FAQ_PAYMENTS = {
  q: 'Where does ticket revenue go?',
  a: 'To the Stripe or Square account you connect in settings, so charges settle directly to your organization.',
};

const FAQ_START = {
  q: 'How do I get started?',
  a: 'Create a free account, add your organization, and create your first event. The user guide walks through each step with screenshots.',
};

const FAQ_RAFFLE_RULES = {
  q: 'Are raffles allowed for our organization?',
  a: 'Raffle and gaming rules vary by state and country, and many require a license or permit. Check with your state’s gambling control or charitable gaming office before running one.',
};

const FAQ_NONPROFIT_DISCOUNT = {
  q: 'Is there a nonprofit discount?',
  a: 'Yes. Registered nonprofits can apply from Billing with their EIN or charity registration number. See the pricing page for the current discount.',
};

// Pages ---------------------------------------------------------------------

export const EVENT_PAGES = [
  {
    slug: 'event-management-software',
    group: 'feature',
    nav: true,
    navLabel: 'Event management',
    relatedTitle: 'Event management software',
    title: 'Event Management Software for Small Organizations | On-Screen',
    description:
      'Create events, collect registrations, sell tickets, check guests in with QR codes, and promote it all on social media. One platform for small businesses, nonprofits, and community organizations.',
    eyebrow: 'Event management',
    h1: 'Event Management Software for Small Businesses, Nonprofits, and Community Organizations',
    intro:
      'Plan the event, sell the tickets, run the door, and promote it — from one dashboard instead of a separate tool for each step.',
    workflowTitle: 'How an event runs in On-Screen',
    workflow: [STEP_CREATE, STEP_REGISTER, STEP_TICKETS, STEP_PROMOTE, STEP_ATTENDEES, STEP_CHECKIN, STEP_AFTER],
    highlights: {
      title: 'More than a ticket form',
      items: [
        { title: 'Auctions, raffles, and contests', body: 'Run them as items inside the same event, with entries, bids, and winners in one results view.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
        { title: 'Team roles and tasks', body: 'Give staff admin, manager, or operator access and assign event tasks with due dates.', doc: DOC('OnScreen_27_My_Tasks.html') },
        { title: 'Membership directory', body: 'Keep donors, volunteers, repeat customers, and ticket holders in one place, with a member portal for their tickets and event history.', doc: DOC('OnScreen_12_Membership.html') },
        { title: 'Social scheduling', body: 'Schedule posts, answer comments and DMs from one inbox, and see engagement analytics.', doc: DOC('OnScreen_22_Calendar_Scheduled_Posts.html') },
      ],
    },
    faq: [FAQ_ACCOUNT, FAQ_DEVICES, FAQ_MODULAR, FAQ_PAYMENTS, FAQ_START],
    related: ['event-registration-software', 'event-ticketing-software', 'nonprofit-event-management-software'],
  },

  {
    slug: 'event-registration-software',
    group: 'feature',
    nav: true,
    navLabel: 'Registration',
    relatedTitle: 'Event registration software',
    title: 'Event Registration Software with Custom Forms and QR Check-In | On-Screen',
    description:
      'Online event registration with custom questions, waitlists, QR code tickets, walk-in registration, and a live guest roster. No attendee login required.',
    eyebrow: 'Event registration',
    h1: 'Event Registration Software for Small Organizations',
    intro:
      'Share one link, collect exactly the information you need, and walk into the event with a roster that’s already sorted.',
    workflowTitle: 'From sign-up to the door',
    workflow: [
      { title: 'Publish the event page', body: 'Your public event page shows the hero image, date, venue, and description. Share it by link or QR code on social media, your website, or a printed flyer.', doc: DOC('OnScreen_30_Public_Event_Attendee_Pages.html') },
      STEP_REGISTER,
      { title: 'Handle capacity', body: 'Each ticket tier has its own capacity. When a tier fills up, registrants can join the waitlist instead of leaving.', doc: DOC('OnScreen_30_Public_Event_Attendee_Pages.html') },
      { title: 'Confirm by email', body: 'Registrants get a confirmation email that opens their ticket wallet — one QR code per ticket, transferable to someone else.', doc: DOC('OnScreen_30_Public_Event_Attendee_Pages.html') },
      { title: 'Work from the roster', body: 'Every registrant and their answers in one filterable list, with check-in status updating live at the door.', doc: DOC('OnScreen_20_Ticketing_Check_in.html') },
      { ...STEP_CHECKIN, title: 'Check in and register walk-ins' },
    ],
    highlights: {
      title: 'Registration details that matter',
      items: [
        { title: 'Custom fields', body: 'Required or optional questions beyond name, email, and phone.' },
        { title: 'No attendee accounts', body: 'People register from the public page without creating a login.' },
        { title: 'Comp registrations', body: 'Send a free ticket to a speaker, sponsor, or volunteer by email.' },
        { title: 'Member portal', body: 'Members can sign in to see their own tickets and event history.', doc: DOC('OnScreen_32_The_Member_Portal.html') },
      ],
    },
    faq: [
      FAQ_ACCOUNT,
      { q: 'Can I ask custom questions during registration?', a: 'Yes. Add any number of custom fields and mark each one required or optional.' },
      { q: 'What happens when the event is full?', a: 'Registrants can join the waitlist for a sold-out tier from the same page.' },
      { q: 'Can I register people at the door?', a: 'Yes. Staff can add walk-ins from the check-in console with their name, contact info, tier, and ticket count.' },
      FAQ_START,
    ],
    related: ['event-management-software', 'event-ticketing-software', 'nonprofit-event-management-software'],
  },

  {
    slug: 'event-ticketing-software',
    group: 'feature',
    nav: true,
    navLabel: 'Ticketing',
    relatedTitle: 'Event ticketing software',
    title: 'Event Ticketing Software for Small Organizations | On-Screen',
    description:
      'Sell event tickets with priced tiers, capacity limits, promo codes, and waitlists. QR code tickets, camera check-in, and a revenue dashboard. Payments go to your own Stripe or Square account.',
    eyebrow: 'Event ticketing',
    h1: 'Event Ticketing Software for Small Organizations',
    intro:
      'Set up tiers, share the link, and watch sales and check-ins in one dashboard — with revenue settling to your own payment account.',
    workflowTitle: 'How ticketing works',
    workflow: [
      { title: 'Turn on ticketing', body: 'Enable ticketing on any event item. Shows, sessions, product releases, demos, and fundraisers all work the same way.', doc: DOC('OnScreen_20_Ticketing_Check_in.html') },
      { title: 'Add ticket tiers', body: 'General admission, VIP, early access — each tier with its own price and capacity, and a live sales count.', doc: DOC('OnScreen_20_Ticketing_Check_in.html') },
      { title: 'Add promo codes', body: 'Fixed-amount or percentage discounts with an optional cap on redemptions.', doc: DOC('OnScreen_20_Ticketing_Check_in.html') },
      { title: 'Share the purchase page', body: 'Buyers pick a tier and quantity, answer your registration questions, and pay. No account needed.', doc: DOC('OnScreen_30_Public_Event_Attendee_Pages.html') },
      { title: 'Deliver QR tickets', body: 'Each ticket gets its own QR code in the buyer’s ticket wallet, and tickets can be transferred to another person.', doc: DOC('OnScreen_30_Public_Event_Attendee_Pages.html') },
      { ...STEP_CHECKIN, title: 'Scan at the door' },
      { title: 'Track revenue and attribution', body: 'Tickets sold per tier, total revenue, and where buyers came from — in one dashboard and in the Event Hub on the day.', doc: DOC('OnScreen_29_The_Event_Hub.html') },
    ],
    highlights: {
      title: 'Built for the night of the event',
      items: [
        { title: 'Your payment account', body: 'Connect Stripe or Square so charges settle directly to you.', doc: DOC('OnScreen_25_Settings_Detail.html') },
        { title: 'Waitlist', body: 'Sold-out tiers collect a waitlist instead of turning people away.' },
        { title: 'Comp tickets', body: 'Email a free ticket; it only activates after the recipient confirms.' },
        { title: 'Walk-in sales', body: 'Charge or comp a tier at the door from the check-in console.' },
      ],
    },
    faq: [
      FAQ_PAYMENTS,
      { q: 'Can I offer discounts?', a: 'Yes. Promo codes can take a fixed amount or a percentage off, with an optional limit on how many times each code is used.' },
      { q: 'Can a buyer give their ticket to someone else?', a: 'Yes. Tickets can be transferred from the buyer’s ticket wallet.' },
      FAQ_DEVICES,
      FAQ_START,
    ],
    related: ['event-management-software', 'event-registration-software', 'nonprofit-event-management-software'],
  },

  {
    slug: 'nonprofit-event-management-software',
    group: 'audience',
    navLabel: 'Nonprofits',
    relatedTitle: 'Nonprofit event management',
    title: 'Nonprofit Event Management Software for Fundraisers and Galas | On-Screen',
    description:
      'Run fundraising events with ticketing, silent auctions, raffles, donor and volunteer records, QR check-in, and social promotion — in one affordable platform for nonprofits.',
    eyebrow: 'For nonprofits',
    h1: 'Event Management Software for Nonprofits',
    intro:
      'Galas, benefit shows, auctions, and raffles take a lot of moving parts. Keep tickets, bidding, supporters, volunteers, and promotion in one place.',
    workflowTitle: 'A fundraiser, start to finish',
    workflow: [
      { ...STEP_CREATE, title: 'Build the fundraiser', body: 'Create the event and add items for each part of it — admission tickets, silent auction lots, a raffle, a contest.' },
      { title: 'Sell tickets and sponsorship tiers', body: 'Priced tiers with capacity, promo codes for board members or partners, and complimentary tickets for sponsors and volunteers sent by email.', doc: DOC('OnScreen_20_Ticketing_Check_in.html') },
      { title: 'Run auctions and raffles', body: 'Supporters place bids and enter raffles online. Entries and bids land in one results view, where winners are drawn or awarded.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
      { title: 'Coordinate volunteers and staff', body: 'Give team members role-based access, assign tasks with due dates, and message them inside the platform.', doc: DOC('OnScreen_17_Team_Collaboration.html') },
      { title: 'Promote with a campaign', body: 'Set goals, and On-Screen plans social posts backward from the event date. Results roll up as goal versus actual.', doc: DOC('OnScreen_28_Marketing_Campaigns.html') },
      STEP_CHECKIN,
      { title: 'Report back to supporters', body: 'Track revenue, vendors, and budget in the Event Hub, then publish a recap page with photos so donors can see what their support made possible.', doc: DOC('OnScreen_34_Event_History_Article.html') },
    ],
    highlights: {
      title: 'Why nonprofits use one platform',
      items: [
        { title: 'Donor and volunteer records', body: 'Members get a profile and a public membership card.', doc: DOC('OnScreen_12_Membership.html') },
        { title: 'Event history', body: 'Supporters can browse photos and clips from past events and comment on them.', doc: DOC('OnScreen_18_Member_Event_History.html') },
        { title: 'Fewer subscriptions', body: 'Ticketing, auctions, raffles, membership, and social media without stitching tools together.' },
        { title: 'Pick what you need', body: 'Start with ticketing or auctions and add the rest later.' },
      ],
    },
    faq: [
      { q: 'Can I run a silent auction and sell tickets for the same event?', a: 'Yes. Each event holds multiple items, so admission tickets, auction lots, and a raffle can live under one event.' },
      FAQ_RAFFLE_RULES,
      { q: 'Can volunteers help run check-in?', a: 'Yes. Add them as team users with an operator role and they can scan tickets from their own phone.' },
      FAQ_PAYMENTS,
      FAQ_NONPROFIT_DISCOUNT,
    ],
    related: ['raffle-software', 'silent-auction-software', 'membership-management-software'],
  },

  {
    slug: 'raffle-software',
    group: 'feature',
    nav: true,
    navLabel: 'Raffles',
    relatedTitle: 'Online raffle software',
    category: 'Raffle software',
    title: 'Online Raffle Software for Nonprofits, Schools, and Churches | On-Screen',
    description:
      'Sell raffle entries online, cap entries per purchase, draw winners at random, and email them automatically. Raffle money goes to your own Stripe or Square account.',
    eyebrow: 'Raffles',
    h1: 'Online Raffle Software for Nonprofits and Community Groups',
    intro:
      'Share one link, sell entries, and draw winners from a list you can trust. No spreadsheets or paper ticket stubs needed.',
    workflowTitle: 'How a raffle runs in On-Screen',
    workflow: [
      { title: 'Add a raffle to your event', body: 'A raffle is an item inside an event, so it can run next to admission tickets, an auction, or a contest for the same fundraiser.', doc: DOC('OnScreen_11_Events.html') },
      { title: 'Set the price and limits', body: 'Charge per entry, or make the first entry free and charge for extras. Set the most entries one person can buy at a time, and how many winners to draw.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
      { title: 'Share the entry page', body: 'Post the public entry link or QR code on social media, in a newsletter, or on a printed flyer at the event. Supporters enter without creating an account.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
      { title: 'Watch entries come in', body: 'The raffle page shows its settings and live statistics next to the full table of entrants.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
      { title: 'Draw the winners', body: 'Run the raffle when you are ready, or schedule the draw for a set time. Winners are picked at random from the entries.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
      { title: 'Notify winners', body: 'Winners get an email, and the results view keeps the list of entrants and winners for your records.', doc: DOC('OnScreen_36_Emails_On_Screen_Sends.html') },
    ],
    highlights: {
      title: 'Raffle details that matter',
      items: [
        { title: 'Paid or free-first entries', body: 'Sell every entry, or give each person one free entry and charge for the rest.' },
        { title: 'Your payment account', body: 'Entry sales settle to the Stripe or Square account you connect.', doc: DOC('OnScreen_25_Settings_Detail.html') },
        { title: 'Raffles in one list', body: 'Every raffle, auction, and contest across your events in one index, filterable by type.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
        { title: 'Reach entrants again', body: 'Group everyone who entered a raffle into a segment and email them about your next event.', doc: DOC('OnScreen_40_Audience_segments_and_messages.html') },
      ],
    },
    faq: [
      FAQ_RAFFLE_RULES,
      { q: 'How are winners picked?', a: 'At random from the raffle’s entries. Each entry a person buys counts toward their chances. You choose how many winners to draw.' },
      { q: 'Can I limit how many entries one person buys?', a: 'Yes. Set a cap on entries per purchase, from 1 to 100.' },
      { q: 'Do people need an account to enter?', a: 'No. Supporters enter from the public raffle page with their name and email.' },
      FAQ_PAYMENTS,
    ],
    related: ['silent-auction-software', 'nonprofit-event-management-software', 'school-event-management-software'],
  },

  {
    slug: 'silent-auction-software',
    group: 'feature',
    nav: true,
    navLabel: 'Auctions',
    relatedTitle: 'Silent auction software',
    category: 'Auction software',
    title: 'Silent Auction Software and Online Bidding for Fundraisers | On-Screen',
    description:
      'Run silent and online auctions for school, church, and nonprofit fundraisers. Supporters bid from their phone, you set starting bids and closing times, and winners get an email.',
    eyebrow: 'Auctions',
    h1: 'Silent Auction Software for School, Church, and Nonprofit Fundraisers',
    intro:
      'Replace clipboards and bid sheets with an auction page supporters open on their phone, whether they are in the room or at home.',
    workflowTitle: 'How an auction runs in On-Screen',
    workflow: [
      { title: 'Add auction items to the event', body: 'Each lot is an item in your event, with its own photos and description. The same event can sell admission tickets and run a raffle too.', doc: DOC('OnScreen_11_Events.html') },
      { title: 'Set the starting bid and timing', body: 'Choose a starting bid and how many winners each lot has, then open bidding now or schedule it to open and close at set times.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
      { title: 'Share the bidding link', body: 'Put the public bidding link or QR code on table cards, the event screen, and social media. Bidders enter their name, email, and bid without creating an account.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
      { title: 'Follow the bidding', body: 'The results view shows the number of bids and the highest bid, with every bid listed newest first.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
      { title: 'Award the winners', body: 'When bidding closes, award the lot and send winners an email from the same screen.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
    ],
    highlights: {
      title: 'Built for fundraiser night',
      items: [
        { title: 'Bids from any phone', body: 'The bidding page works in any browser. Nothing to install for bidders.' },
        { title: 'One event, every fundraiser', body: 'Tickets, auction lots, raffles, and contests live under one event.', doc: DOC('OnScreen_11_Events.html') },
        { title: 'On the calendar', body: 'Live auctions and raffles show on the same calendar as your events and scheduled posts.', doc: DOC('OnScreen_22_Calendar_Scheduled_Posts.html') },
        { title: 'Promote the lots', body: 'Schedule posts featuring your best items to your connected social accounts.', doc: DOC('OnScreen_28_Marketing_Campaigns.html') },
      ],
    },
    faq: [
      { q: 'Can people bid from home as well as at the event?', a: 'Yes. Anyone with the bidding link can place a bid while the auction is open.' },
      { q: 'Does On-Screen collect payment for winning bids?', a: 'Not yet. Winners get an email, and you collect payment the way you do today, such as at checkout on the night or by invoice.' },
      { q: 'Can I set a minimum bid?', a: 'Yes. Each lot can have a starting bid, and bids below it are rejected.' },
      { q: 'Can I run an auction and sell tickets for the same event?', a: 'Yes. Each event holds multiple items, so admission tickets, auction lots, and a raffle can live under one event.' },
      FAQ_START,
    ],
    related: ['raffle-software', 'school-event-management-software', 'nonprofit-event-management-software'],
  },

  {
    slug: 'membership-management-software',
    group: 'feature',
    nav: true,
    navLabel: 'Membership',
    relatedTitle: 'Membership management software',
    category: 'Membership management software',
    finalTitle: 'Bring your members together',
    title: 'Membership Management Software for Small Organizations | On-Screen',
    description:
      'Keep members, donors, volunteers, and ticket buyers in one searchable directory. Members get a portal with their tickets and event history, and you can email them by segment.',
    eyebrow: 'Membership',
    h1: 'Membership Management for Small Organizations',
    intro:
      'One list of the people who make your organization work, connected to the events they attend and the messages you send them.',
    workflowTitle: 'How membership works in On-Screen',
    workflow: [
      { title: 'Add members', body: 'Record each member’s name, email, phone, and address, with a photo you upload or take with the device camera.', doc: DOC('OnScreen_12_Membership.html') },
      { title: 'Find anyone quickly', body: 'Search the member list by name or contact details, and open any member to update their record.', doc: DOC('OnScreen_12_Membership.html') },
      { title: 'Give members a portal', body: 'Members sign in with the email you registered them with and choose a password. No invite email needed. They see every ticket they have bought, with QR codes and calendar links.', doc: DOC('OnScreen_32_The_Member_Portal.html') },
      { title: 'Share event history', body: 'Members browse photos and clips from past events and comment on them.', doc: DOC('OnScreen_18_Member_Event_History.html') },
      { title: 'Collect sign-ups with forms', body: 'Build a public form for volunteer sign-ups, interest lists, or applications, and embed it on your website.', doc: DOC('OnScreen_39_Forms.html') },
      { title: 'Message the right people', body: 'Group people who bought tickets, checked in, entered raffles, bid in auctions, or filled in your forms, then send them an email or push notification. Unsubscribes and quiet hours are handled for you.', doc: DOC('OnScreen_40_Audience_segments_and_messages.html') },
    ],
    highlights: {
      title: 'Membership connected to everything else',
      items: [
        { title: 'Membership card', body: 'Each member has a profile and a public membership card.', doc: DOC('OnScreen_12_Membership.html') },
        { title: 'Events and tickets', body: 'Ticket purchases show up in the member’s portal automatically.', doc: DOC('OnScreen_32_The_Member_Portal.html') },
        { title: 'Team access', body: 'Give staff and volunteers their own logins with admin, manager, or operator roles.', doc: DOC('OnScreen_13_Tenant_Users.html') },
        { title: 'Social media too', body: 'Schedule posts and answer comments from the same platform.', doc: DOC('OnScreen_22_Calendar_Scheduled_Posts.html') },
      ],
    },
    faq: [
      { q: 'Who is this for?', a: 'Clubs, churches, schools, nonprofits, and small businesses that keep a list of members, supporters, or regular customers and run events for them.' },
      { q: 'Can On-Screen bill recurring membership dues?', a: 'Not today. On-Screen keeps member records, the member portal, and messaging. Ticket and raffle sales go through the payment account you connect.' },
      { q: 'Do members need to install anything?', a: 'No. The member portal works in any browser, and members can install it as an app if they like.' },
      FAQ_MODULAR,
      FAQ_START,
    ],
    related: ['event-management-software', 'church-event-management-software', 'social-media-scheduling-for-nonprofits'],
  },

  {
    slug: 'social-media-scheduling-for-nonprofits',
    group: 'feature',
    nav: true,
    navLabel: 'Social media',
    relatedTitle: 'Social media scheduling for nonprofits',
    category: 'Social media management software',
    finalTitle: 'Plan your next post',
    title: 'Social Media Scheduling for Nonprofits and Small Organizations | On-Screen',
    description:
      'Schedule posts to Facebook, Instagram, X, LinkedIn, Bluesky, YouTube, and more. Answer comments and DMs from one inbox, approve posts before they go out, and plan event promotion automatically.',
    eyebrow: 'Social media',
    h1: 'Social Media Scheduling for Nonprofits and Small Organizations',
    intro:
      'Write once, post everywhere, and keep every comment and message in one inbox, with your events on the same calendar.',
    workflowTitle: 'How social posting works in On-Screen',
    workflow: [
      { title: 'Connect your accounts', body: 'Link Facebook, Instagram, X, LinkedIn, Bluesky, YouTube, and other platforms from Integrations.', doc: DOC('OnScreen_14_Social_Media_Integrations.html') },
      { title: 'Write the post', body: 'Photos, videos, carousels, or text. Pick which platforms it goes to, and the character counter follows the strictest one you choose.', doc: DOC('OnScreen_10_Post.html') },
      { title: 'Schedule it', body: 'Post now, pick a date and time, or repeat a post daily across a date range. Everything coming up shows on one calendar next to your events.', doc: DOC('OnScreen_22_Calendar_Scheduled_Posts.html') },
      { title: 'Approve before it goes out', body: 'Volunteers and staff submit posts for review. A manager approves, requests changes, or rejects them, and editing an approved post sends it back for review.', doc: DOC('OnScreen_43_Approvals.html') },
      { title: 'Plan event promotion', body: 'A marketing campaign plans posts backward from the event date: an announcement, the main push, a final call, and a recap.', doc: DOC('OnScreen_28_Marketing_Campaigns.html') },
      { title: 'Answer from one inbox', body: 'Comments, DMs, and mentions from every connected platform in one inbox. Filter by platform or assignee and reply from the same screen.', doc: DOC('OnScreen_09_Social_Inbox.html') },
      { title: 'See what worked', body: 'Followers, engagement, and reach across your platforms for any date range.', doc: DOC('OnScreen_08_Analytics.html') },
    ],
    highlights: {
      title: 'Why organizations post from On-Screen',
      items: [
        { title: 'Events and posts together', body: 'Promotion is tied to the event it is for, so ticket sales and check-ins count toward the campaign’s goals.', doc: DOC('OnScreen_28_Marketing_Campaigns.html') },
        { title: 'Safe for volunteers', body: 'Role-based access and approvals mean nothing goes out without a manager’s sign-off.', doc: DOC('OnScreen_43_Approvals.html') },
        { title: 'Tracked links', body: 'See which posts and links brought in visitors and ticket buyers.', doc: DOC('OnScreen_37_Tracked_Links_and_Performance.html') },
        { title: 'Nonprofit discount', body: 'Registered nonprofits can apply for a discount on any plan.', doc: DOC('OnScreen_45_Nonprofit_Discount.html') },
      ],
    },
    faq: [
      { q: 'Which platforms can I post to?', a: 'Facebook, Instagram, X, LinkedIn, Bluesky, YouTube, and more. The Integrations page lists every platform you can connect.' },
      { q: 'Can several people post for our organization?', a: 'Yes. Add team members with their own logins and roles, and turn on approvals so a manager reviews posts first.' },
      { q: 'Can I use only the social media tools?', a: 'Yes. Use the scheduler and inbox on their own, and add events, ticketing, or membership later.' },
      FAQ_NONPROFIT_DISCOUNT,
      FAQ_START,
    ],
    related: ['event-management-software', 'nonprofit-event-management-software', 'membership-management-software'],
  },

  {
    slug: 'church-event-management-software',
    group: 'audience',
    navLabel: 'Churches',
    relatedTitle: 'Church event management',
    title: 'Church Event Management and Ticketing Software | On-Screen',
    description:
      'Registration and ticketing for church events, retreats, dinners, and fundraisers. QR check-in run by volunteers, a member directory, raffles and auctions, and social media in one place.',
    eyebrow: 'For churches',
    h1: 'Event Management and Ticketing for Churches',
    intro:
      'Retreats, dinners, concerts, youth trips, and fundraisers, with sign-ups, tickets, volunteers, and announcements in one place.',
    workflowTitle: 'A church event, start to finish',
    workflow: [
      { ...STEP_CREATE, title: 'Create the event', body: 'Add the event with its date, location, and description. Add items for each part, such as dinner tickets, a raffle, or a silent auction.' },
      { ...STEP_REGISTER, title: 'Open sign-ups', body: 'Collect name, email, and phone, plus custom questions such as dietary needs, T-shirt size, a child’s age, or an emergency contact.' },
      { ...STEP_TICKETS, title: 'Sell tickets or keep it free', body: 'Use free registration, or priced tiers with capacity limits and a waitlist. Payments go to the Stripe or Square account you connect.' },
      { title: 'Let volunteers run the door', body: 'Give volunteers an operator login. They scan QR tickets with their own phone and register walk-ins on the spot.', doc: DOC('OnScreen_20_Ticketing_Check_in.html') },
      { title: 'Announce it', body: 'Schedule posts to your church’s Facebook and Instagram, and email or push-message the people who came last time.', doc: DOC('OnScreen_40_Audience_segments_and_messages.html') },
      { title: 'Share the memories', body: 'Publish a recap page with photos from the event, including ones a volunteer photographer uploads through a link without an account.', doc: DOC('OnScreen_34_Event_History_Article.html') },
    ],
    highlights: {
      title: 'Made for congregations',
      items: [
        { title: 'Member directory', body: 'Keep members and visitors in one searchable list, with a portal for their tickets.', doc: DOC('OnScreen_12_Membership.html') },
        { title: 'Fundraisers', body: 'Run raffles and silent auctions inside the same event.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
        { title: 'Volunteer tasks', body: 'Assign set-up, food, and clean-up tasks with due dates.', doc: DOC('OnScreen_27_My_Tasks.html') },
        { title: 'Pick what you need', body: 'Start with registration and add the rest later.' },
      ],
    },
    faq: [
      { q: 'Can we run free events with registration only?', a: 'Yes. Collect sign-ups and custom answers without charging, and still check people in with QR codes.' },
      FAQ_ACCOUNT,
      { q: 'Can volunteers help run check-in?', a: 'Yes. Add them as team users with an operator role and they can scan tickets from their own phone.' },
      FAQ_NONPROFIT_DISCOUNT,
      FAQ_START,
    ],
    related: ['event-registration-software', 'membership-management-software', 'raffle-software'],
  },

  {
    slug: 'school-event-management-software',
    group: 'audience',
    navLabel: 'Schools',
    relatedTitle: 'School event and auction software',
    title: 'School Event, Auction, and Fundraiser Software for PTAs | On-Screen',
    description:
      'Run school auctions, raffles, carnivals, concerts, and family nights. Sell tickets, take bids from parents’ phones, check guests in with QR codes, and promote it on social media.',
    eyebrow: 'For schools and PTAs',
    h1: 'School Event and Auction Software for PTAs and Booster Clubs',
    intro:
      'From the spring auction to the fall concert, run tickets, bidding, raffles, and volunteers from one place that parents can open on their phones.',
    workflowTitle: 'A school fundraiser, start to finish',
    workflow: [
      { ...STEP_CREATE, title: 'Set up the fundraiser', body: 'Create the event and add items for each part, such as admission, auction lots, a raffle, or a contest.' },
      { title: 'Take auction bids from phones', body: 'Parents bid from the auction page with their name and email. Set a starting bid for each lot and a closing time.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
      { title: 'Sell raffle entries', body: 'Charge per entry or make the first one free, cap entries per purchase, and draw winners at random.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
      { ...STEP_TICKETS, title: 'Sell tickets for concerts and family nights' },
      { title: 'Organize volunteers', body: 'Give parent volunteers their own login and assign tasks with due dates.', doc: DOC('OnScreen_27_My_Tasks.html') },
      { ...STEP_CHECKIN, title: 'Check families in at the door' },
      { title: 'Report the results', body: 'The Event Hub shows revenue, tickets sold, and check-ins, ready for the next board meeting.', doc: DOC('OnScreen_29_The_Event_Hub.html') },
    ],
    highlights: {
      title: 'Why PTAs use one platform',
      items: [
        { title: 'Auctions and raffles', body: 'Bids, entries, and winners in one results view.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
        { title: 'Promotion built in', body: 'Schedule posts to your school’s social accounts, with approvals so nothing goes out unreviewed.', doc: DOC('OnScreen_43_Approvals.html') },
        { title: 'Parent forms', body: 'Volunteer sign-ups and interest forms you can embed on the school website.', doc: DOC('OnScreen_39_Forms.html') },
        { title: 'No accounts for parents', body: 'Parents buy tickets, bid, and enter raffles without creating a login.' },
      ],
    },
    faq: [
      { q: 'Can parents bid from home?', a: 'Yes. Anyone with the auction link can bid while bidding is open, whether they are at the event or not.' },
      { q: 'Does On-Screen collect payment for winning auction bids?', a: 'Not yet. Winners get an email, and you collect payment the way you do today. Ticket and raffle sales are paid online to the account you connect.' },
      FAQ_RAFFLE_RULES,
      FAQ_DEVICES,
      FAQ_START,
    ],
    related: ['silent-auction-software', 'raffle-software', 'event-ticketing-software'],
  },
];

export const FEATURE_PAGES = EVENT_PAGES.filter((p) => p.group === 'feature');
export const AUDIENCE_PAGES = EVENT_PAGES.filter((p) => p.group === 'audience');
export const NAV_PAGES = EVENT_PAGES.filter((p) => p.nav);

export const pageBySlug = (slug) => EVENT_PAGES.find((p) => p.slug === slug);

export const pageUrl = (page) => `${SITE}/${page.slug}`;

// Structured data -----------------------------------------------------------

export function buildJsonLd(page) {
  const url = pageUrl(page);
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'On-Screen',
      url,
      applicationCategory: 'BusinessApplication',
      applicationSubCategory: page.category || 'Event management software',
      operatingSystem: 'Web, iOS, Android, Windows, macOS',
      description: page.description,
      publisher: { '@type': 'Organization', name: 'SE3D Inc.' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: page.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'On-Screen', item: `${SITE}/about` },
        { '@type': 'ListItem', position: 2, name: page.navLabel, item: url },
      ],
    },
  ];
}
